import { Box, Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useGetSavedServiceList } from '../../../services/business/savedService';
import { useGetShopServices } from '../../../services/business/services';
import useSavedServiceStore from '../../../store/useSavedServiceStore';
import SavedServicesFilters from './SavedServiceList/SavedServicesFilters';
import SavedServiceTable from './SavedServiceList/SavedServiceTable';

// Lazy load the modal component for better performance
// This reduces the initial bundle size and only loads the modal when needed
const SavedServicesAddModal = lazy(() => import('./SavedServicesAddModal/SavedServicesAddModal'));

const SavedServices = () => {
    const { savedServiceParams, isModalOpen, selectedSavedServiceId, setSavedServiceParams, openModal, closeModal, showLoader, setShowLoader } =
        useSavedServiceStore();

    // Local state to track mutation loading
    const [isMutationLoading, setIsMutationLoading] = useState(false);

    const { data: shopServicesData, isLoading: shopServicesLoading } = useGetShopServices({
        pagination: false,
        limit: 2500,
        sortBy: 'name',
        sortType: 'ASC',
    });

    // TanStack Query hook for fetching saved services
    const { data: savedServiceData, isLoading: initialLoading, error, refetch: refetchSavedServices } = useGetSavedServiceList(savedServiceParams);

    // Extract data from response
    const savedServiceList = savedServiceData?.data || [];
    const savedServiceListPagination = {
        totalCount: savedServiceData?.totalCount || 0,
        totalPages: savedServiceData?.totalPages || 0,
        currentPage: savedServiceParams?.page || 1,
    };

    // Enhanced refetch function that shows loader during refetch
    const refreshSavedServices = useCallback(async () => {
        try {
            setIsMutationLoading(true);
            setShowLoader(true);
            await refetchSavedServices();
        } catch (error) {
            console.error('Error refreshing saved services:', error);
        } finally {
            // Add a small delay to ensure the UI updates properly
            setTimeout(() => {
                setIsMutationLoading(false);
                setShowLoader(false);
            }, 500);
        }
    }, [refetchSavedServices, setShowLoader]);

    // Combined loading state - show loader for initial load OR mutation operations
    const showLoaderState = initialLoading || isMutationLoading || showLoader;

    useEffect(() => {
        return () => {
            setSavedServiceParams({
                ...savedServiceParams,
                page: 1,
                sortOrder: 'desc',
                sortBy: 'serviceName',
                vehicleMake: [],
                serviceCategory: [],
            });
        };
    }, [setSavedServiceParams]);

    // Handle error
    if (error) {
        console.error('Error fetching saved services:', error);
    }

    return (
        <>
            <Stack gap={3} mt={4}>
                <SavedServicesFilters savedServiceParams={savedServiceParams} setSavedServiceParams={setSavedServiceParams} />
                <Box bgcolor={'white'} boxShadow={3} borderRadius={1} padding={3}>
                    <Stack justifyContent={'space-between'} direction={'row'} mb={3}>
                        <Typography variant="h5">Saved Services</Typography>
                        <Button color="secondary" variant="contained" onClick={() => openModal()}>
                            add new service
                        </Button>
                    </Stack>
                    <SavedServiceTable
                        openModal={openModal}
                        savedServiceList={savedServiceList}
                        savedServiceParams={savedServiceParams}
                        setSavedServiceParams={setSavedServiceParams}
                        savedServiceListPagination={savedServiceListPagination}
                        refreshSavedServices={refreshSavedServices}
                    />
                </Box>
            </Stack>

            {/* Lazy loaded modal with Suspense and ErrorBoundary */}
            {isModalOpen && (
                <Suspense fallback={<CircularProgress />}>
                    <SavedServicesAddModal
                        selectedSavedServiceId={selectedSavedServiceId}
                        isOpenModal={isModalOpen}
                        onClose={closeModal}
                        shopServicesData={shopServicesData}
                        shopServicesLoading={shopServicesLoading}
                        refreshSavedServices={refreshSavedServices}
                    />
                </Suspense>
            )}

            {/* <AddSavedServicesModal openModal={false} onClose={() => {}} /> */}

            <Dialog fullScreen={false} open={showLoaderState}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default React.memo(SavedServices);
